import { UserCheck, ShieldCheck } from 'lucide-react';
import { automodProtections } from '@/data/automod';
import { Card, CardHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { ModuleTogglePanel } from '@/components/dashboard/ModuleTogglePanel';

const actionTone = { warn: 'management', timeout: 'security', kick: 'security', ban: 'security' } as const;

/**
 * label/description/threshold/action below are still real, bot-accurate
 * display metadata (cogs/advanced_automod.py's DEFAULTS) — only the
 * enabled/disabled STATE now comes from the live API via ModuleTogglePanel;
 * `enabledByDefault` from data/automod.ts is unused here since the real
 * value replaces it.
 *
 * Threshold/action remain genuinely read-only: bridge/config_registry.py's
 * "automod" module only accepts {"states": {feature: bool}} — sending
 * limit_value/punishment is rejected 422 server-side (there is no write
 * path for them anywhere in v18), so no edit control exists for them here.
 */
export function AutomodPage() {
  return (
    <div className="max-w-3xl space-y-6 pb-24">
      <div>
        <h1 className="text-h1 text-text-primary mb-1">AutoMod</h1>
        <p className="text-small text-text-secondary">
          12 independent protections, each with a threshold and punishment action set by the bot.
        </p>
      </div>

      <ModuleTogglePanel
        module="automod"
        title="Protections"
        cardDescription="Threshold and action are set by the bot and aren't editable here — v18 only supports enable/disable via commands today."
        features={automodProtections.map((p) => ({
          key: p.key,
          label: (
            <span className="flex items-center gap-2 flex-wrap">
              {p.label}
              <Badge tone={actionTone[p.action]}>{p.action}</Badge>
              <span className="text-micro text-text-tertiary font-normal normal-case">threshold {p.threshold}</span>
            </span>
          ),
          description: p.description,
        }))}
      />

      <Card>
        <CardHeader
          title="Verification"
          description="Member verification (CAPTCHA)"
          action={<Badge tone="neutral">Not configurable yet</Badge>}
        />
        <div className="flex items-start gap-3">
          <UserCheck size={18} className="text-text-tertiary shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-small text-text-secondary">
            The verify button and its underlying table exist in v18, but no command anywhere
            configures a verification role or channel — so this can't be turned on for any server
            yet, and there is nothing for a config API to expose. See
            docs/V18_ARCHITECTURE_NOTES.md's Database Verification section.
          </p>
        </div>
      </Card>

      <Card>
        <CardHeader title="Whitelist" action={<ShieldCheck size={18} className="text-text-tertiary" aria-hidden="true" />} />
        <p className="text-small text-text-secondary">
          Exempt specific members from AutoMod actions. Not connected yet — this needs a dedicated
          Bridge endpoint beyond this pass's generic enable/disable toggle.
        </p>
      </Card>
    </div>
  );
}
